import { Button, Chip, Divider } from "@nextui-org/react"; // Or your accordion lib
import HayaTableRender from "./HayaTableRender";
import "./response.css";
import toast from "react-hot-toast";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp, CopyIcon } from "lucide-react";
import ShikiHighlighter from "react-shiki";

function stripJsonStringArray(input) {
  try {
    const parsed = JSON.parse(input);
    if (
      Array.isArray(parsed) &&
      parsed.length === 1 &&
      typeof parsed[0] === "string"
    ) {
      return parsed[0];
    }
  } catch {
    // not a JSON array, ignore
  }
  return input;
}

export const renderResponseWithCodeBlocks = (response, responseTable) => {
  const parts = [];
  let inThinkBlock = false;
  let thinkBuffer = "";
  let normalBuffer = "";

  const flushNormal = () => {
    if (normalBuffer) {
      parts.push({ type: "normal", content: normalBuffer });
      normalBuffer = "";
    }
  };

  const flushThink = () => {
    if (thinkBuffer) {
      parts.push({ type: "think", content: thinkBuffer });
      thinkBuffer = "";
    }
  };

  // Stream-safe parsing for <think> blocks
  const lines = response.split("\n");
  for (const line of lines) {
    if (line.includes("<think>")) {
      flushNormal();
      inThinkBlock = true;
      thinkBuffer += line.substring(line.indexOf("<think>") + 7) + "\n";
    } else if (line.includes("</think>")) {
      thinkBuffer += line.substring(0, line.indexOf("</think>")) + "\n";
      flushThink();
      inThinkBlock = false;
    } else if (inThinkBlock) {
      thinkBuffer += line + "\n";
    } else {
      normalBuffer += line + "\n";
    }
  }

  if (inThinkBlock) {
    // <think> was never closed, still render what we have
    parts.push({ type: "think", content: thinkBuffer, unclosed: true });
  } else {
    flushNormal();
  }

  function renderMarkdownText(text) {
    const regex =
      /(\*\*(.*?)\*\*|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|`([^`]+)`|<img\s+src="(https?:\/\/[^"]+)"\s*\/?>)/g;

    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      const [fullMatch, , boldText, linkText, linkUrl, inlineCode, imageUrl] =
        match;

      // Add plain text before match
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }

      if (boldText !== undefined) {
        parts.push(
          <strong key={match.index} className="font-semibold">
            {renderMarkdownText(boldText)}
          </strong>
        );
      } else if (linkText && linkUrl) {
        parts.push(
          <a
            key={match.index}
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-600 hover:underline"
          >
            {linkText}
          </a>
        );
      } else if (inlineCode) {
        parts.push(
          <Chip
            className="mx-1"
            radius="sm"
            key={match.index}
            size="sm"
            variant="flat"
            color="default"
          >
            {stripJsonStringArray(inlineCode)}
          </Chip>
        );
      } else if (imageUrl) {
        parts.push(
          <img
            key={match.index}
            src={imageUrl}
            alt="Embedded content"
            className="inline-block max-h-8 rounded-full my-1 mr-2"
          />
        );
      }

      lastIndex = match.index + fullMatch.length;
    }

    // Add remaining plain text
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts;
  }

  const renderContentBlock = (blockContent, index) => {
    const codeSplitParts = blockContent.split(/(```[\w-]*\n[\s\S]*?```)/g);

    return codeSplitParts.map((part, subIndex) => {
      if (part.startsWith("```")) {
        const match = part.match(/^```(\w+)?\n([\s\S]*?)```$/);
        const language = match ? match[1] || "plaintext" : "plaintext";
        const codeContent = match ? match[2] : part.slice(3, -3).trim();

        const copyToClipboard = () => {
          navigator.clipboard.writeText(codeContent);
          toast("Copied to clipboard!");
        };

        return (
          <div key={`${index}-${subIndex}`} className="custom-code-block">
            <div className="top-bar">
              <span>{language?.toUpperCase()}</span>
              <button className="copy-btn" onClick={copyToClipboard} aria-label="Copy code to clipboard">
                <CopyIcon className="size-4" />
              </button>
            </div>
            {/* <code>{codeContent}</code> */}
            <ShikiHighlighter
              addDefaultStyles={false}
              showLanguage={false}
              language={language}
              theme="ayu-dark"
            >
              {codeContent}
            </ShikiHighlighter>
          </div>
        );
      } else {
        return (
          <div key={`${index}-${subIndex}`}>
            {part.split("\n").map((line, lineIndex) => {
              const trimmed = line.trim();

              // Divider detection
              if (trimmed === "---") {
                return <Divider key={lineIndex} className="my-4" />;
              }

              // Heading detection
              const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)/);
              if (headingMatch) {
                const level = headingMatch[1].length;
                const content = headingMatch[2];
                const HeadingTag = `h${level}`;
                return (
                  <div className="response-block" key={lineIndex}>
                    <HeadingTag key={lineIndex} className="mt-4 mb-2 font-bold">
                      {renderMarkdownText(content)}
                    </HeadingTag>
                  </div>
                );
              }

              // Table detection
              if (trimmed.startsWith("|")) {
                const tablepart = trimmed;
                if (tablepart.includes("----")) {
                  return (
                    <div
                      className="max-h-100px overflow-y-auto"
                      key={lineIndex}
                    >
                      <HayaTableRender markdownTable={responseTable} />
                    </div>
                  );
                } else {
                  responseTable.push(tablepart + "\n");
                  return null;
                }
              }

              // Bullet point
              if (trimmed.startsWith("-")) {
                return (
                  <p key={lineIndex} className="leading-loose">
                    &bull; {renderMarkdownText(trimmed.substring(1).trim())}
                    <br />
                  </p>
                );
              }

              // Default text
              return (
                <p key={lineIndex} className="leading-loose">
                  {renderMarkdownText(trimmed)}
                  <br />
                </p>
              );
            })}
          </div>
        );
      }
    });
  };

  return parts.map((part, index) => {
    if (part.type === "think") {
      return (
        <AccordionButtonComp
          isUnclosed={part.unclosed}
          comp={renderContentBlock(part.content, index)}
        />
      );
    } else {
      return renderContentBlock(part.content, index);
    }
  });
};

const AccordionButtonComp = ({ comp, isUnclosed }) => {
  const [open, setOpen] = useState(true);

  return (
    <div className="flex flex-col mt-2 gap-4">
      <div>
        <Button
          endContent={
            open ? (
              <ChevronUp className=" size-4" />
            ) : (
              <ChevronDown className=" size-4" />
            )
          }
          variant="flat"
          onClick={() => setOpen((prev) => !prev)}
        >
          {isUnclosed ? "Thinking..." : "Reasoning"}
        </Button>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="accordion-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="text-gray flex gap-8">
              {/* Vertical Line */}
              <div className=" pt-2 pb-8">
                <div className="w-[1px] h-full bg-gray" />
              </div>

              {/* Content */}
              <div className="flex-1 pt-2 font-light text-gray-300">{comp}</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
